// Release run report — every per-app run for one releaseKey (the shared
// groupId), each with its per-flow verdicts + evidence videos. Reuses the
// browser-verify AutoVerdict/EvidenceVideo components.
import { useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import Shell from "./Shell";
import { ErrorState, LoadingState, RefreshButton, apiErrorMessage } from "./shared";
import { AutoVerdict } from "./BrowserVerifyEvidence";
import "./qa-flows.css";

const APP_LABEL = { portal: "Portal (PX)", applicant: "Applicant (AX)", crm: "CRM (LX)" };

function tally(executions) {
  const t = {};
  for (const e of executions) t[e.verdict] = (t[e.verdict] || 0) + 1;
  return t;
}

export default function ReleaseRunReport() {
  const { releaseKey } = useParams();
  const fetcher = useCallback(() => api.bssReleaseReport(releaseKey), [releaseKey]);
  const { data, error, loading, refresh } = useBssPoll(fetcher, { deps: [releaseKey] });

  const apps = data?.data?.apps || [];

  return (
    <Shell>
      <div className="bss-page-hd">
        <h1>
          <Link to="/bss/qa-flows" className="bss-link">
            Release flows
          </Link>{" "}
          / Report
        </h1>
        <div className="bss-page-hd-right">
          <RefreshButton onClick={refresh} loading={loading} />
        </div>
      </div>
      <p className="qa-report-key">
        <Icon.Hash size={13} /> {releaseKey}
      </p>

      {loading && !data && <LoadingState />}
      {error && !data && <ErrorState message={apiErrorMessage(error)} onRetry={refresh} />}

      {data && (
        <div className="qa-report">
          {apps.length === 0 && <p className="qa-flows-empty">No runs found for this release.</p>}
          {apps.map((app) => {
            const t = tally(app.executions || []);
            return (
              <div key={app.runId} className="qa-report-app">
                <div className="qa-report-app-hd">
                  <span className="qa-flows-app-name">{APP_LABEL[app.app] || app.app}</span>
                  <span className="qa-report-tally">
                    {Object.entries(t).map(([v, n]) => (
                      <span key={v} className={`qa-tally qa-tally-${v}`}>
                        {v} {n}
                      </span>
                    ))}
                  </span>
                  <Link to={`/bss/runs/${app.runId}`} className="qa-link-btn">
                    Run detail →
                  </Link>
                </div>
                {(app.executions || []).map((e) => (
                  <div key={e.id} className="qa-report-flow">
                    <div className="qa-report-flow-name">{e.title}</div>
                    <AutoVerdict execution={e} />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </Shell>
  );
}
